/**
 * Data layer for `advertiser-macros`. Thin typed fetch wrappers over
 * /api/v1/advertiser-macros. Raw `fetch()` is confined to this file per the
 * dash contract.
 */

export type MacroMap = Record<string, string>;

export interface AdvertiserMacroRow {
	id: string;
	tenant_id: string;
	advertiser_id: string;
	name: string;
	macros: MacroMap;
	created_at: number;
	updated_at: number;
}

export interface CreateAdvertiserMacroBody {
	advertiser_id: string;
	name: string;
	macros: MacroMap;
}

export interface UpdateAdvertiserMacroBody {
	name?: string;
	macros?: MacroMap;
}

export interface RenderResult {
	id: string;
	advertiser_id: string;
	name: string;
	resolved: MacroMap;
}

const BASE = "/api/v1/advertiser-macros";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(advertiser_id: string): Promise<AdvertiserMacroRow[]> {
	const q = new URLSearchParams({ advertiser_id }).toString();
	const res = await fetch(`${BASE}?${q}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<AdvertiserMacroRow[]>(res);
}

export async function create(body: CreateAdvertiserMacroBody): Promise<AdvertiserMacroRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<AdvertiserMacroRow>(res);
}

export async function update(
	id: string,
	body: UpdateAdvertiserMacroBody,
): Promise<AdvertiserMacroRow> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "PATCH",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<AdvertiserMacroRow>(res);
}

export async function remove(id: string): Promise<{ id: string; deleted: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ id: string; deleted: boolean }>(res);
}

export async function render(
	id: string,
	context: Record<string, unknown>,
): Promise<RenderResult> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}/render`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify({ context }),
	});
	return parseOrThrow<RenderResult>(res);
}

export const advertiserMacrosQueryKeys = {
	all: ["advertiser-macros"] as const,
	list: (advertiser_id: string) =>
		["advertiser-macros", advertiser_id] as const,
	detail: (id: string) => ["advertiser-macros", "detail", id] as const,
};

// ---------------------------------------------------------------------------
// Client-side helpers used by the page + tests.
// ---------------------------------------------------------------------------

const TEMPLATE_TOKEN_RE = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;

/**
 * Local mirror of the server-side renderMacros. Used for live preview so the
 * user sees substitution results without a round trip. Same rules: unknown
 * keys stay literal, null/undefined become empty string, non-strings coerce.
 */
export function previewRender(
	macros: MacroMap,
	context: Record<string, unknown>,
): MacroMap {
	const out: MacroMap = {};
	for (const [k, template] of Object.entries(macros)) {
		const str = typeof template === "string" ? template : String(template);
		out[k] = str.replace(TEMPLATE_TOKEN_RE, (match, name) => {
			if (!Object.prototype.hasOwnProperty.call(context, name)) return match;
			const v = context[name];
			if (v === null || v === undefined) return "";
			return String(v);
		});
	}
	return out;
}

/**
 * Filter a list of templates by case-insensitive name/id substring.
 */
export function filterByName(
	rows: AdvertiserMacroRow[],
	query: string,
): AdvertiserMacroRow[] {
	const q = query.trim().toLowerCase();
	if (!q) return rows;
	return rows.filter(
		(r) =>
			r.name.toLowerCase().includes(q) ||
			r.id.toLowerCase().includes(q),
	);
}

/**
 * Parse a newline-delimited `key=value` blob into a MacroMap. Lines without
 * `=` or empty keys are skipped. Values keep embedded `=` signs intact.
 */
export function parseMacrosBlob(blob: string): MacroMap {
	const out: MacroMap = {};
	for (const line of blob.split(/\r?\n/)) {
		const trimmed = line.trim();
		if (!trimmed) continue;
		const eq = trimmed.indexOf("=");
		if (eq <= 0) continue;
		const key = trimmed.slice(0, eq).trim();
		const value = trimmed.slice(eq + 1);
		if (!key) continue;
		out[key] = value;
	}
	return out;
}

export function serializeMacrosBlob(macros: MacroMap): string {
	return Object.entries(macros)
		.map(([k, v]) => `${k}=${v}`)
		.join("\n");
}

/**
 * Parse a newline-delimited context blob the same way.
 */
export function parseContextBlob(blob: string): Record<string, string> {
	return parseMacrosBlob(blob);
}
